import { NormalizedCacheObject } from 'apollo-boost';
import { ApolloQueryResult } from 'apollo-client';

import { dealsCount } from '../../graphql/queries/grid/common';
import { dealsQuery } from '../../graphql/queries/grid/deals';
import {
    mapDealDetailDataToDeal,
    mapDealsQueryResultToEntitiesQueryResponse,
    mapSourceParamsToDealsQueryVariables
} from '../../mappers/deal';
import { DropdownChooserItem } from '../../typings/common';
import { DealDetail, DealsSourceParams, dealStatusForFilter, DealsUpdateObject } from '../../typings/deals';
import { DealsCount, DealsQuery } from '../../typings/graphql-grid';
import { BaseDataProvider } from '../BaseDataProvider/BaseDataProvider';
import { DNAApolloClient } from '../DNAApolloClient/DNAApolloClient';
import { NewDealsCountResponse } from '../QueryExecutor';
import { RestApiManager } from '../RestApiManager';

export class DealsDataProvider extends BaseDataProvider {
    private apiManager: RestApiManager;

    constructor(apiManager: RestApiManager, client: DNAApolloClient<NormalizedCacheObject>) {
        super(client);
        this.apiManager = apiManager;
    }

    public fetchDealsDataSegment = (
        sourceParams: DealsSourceParams,
        offset: number,
        limit: number,
        cacheKey: string
    ) => {
        const query = this.createQuery<DealsQuery>(dealsQuery, {
            variables: {
                ...mapSourceParamsToDealsQueryVariables(sourceParams),
                offset,
                limit,
                cacheKey
            }
        });

        return query.then(mapDealsQueryResultToEntitiesQueryResponse);
    };

    public async getDealsDetail(login: string, dealIds: number[]): Promise<DealDetail[]> {
        return this.apiManager
            .getDetailDeals(login, dealIds)
            .then(detailDeals => detailDeals.map(mapDealDetailDataToDeal));
    }

    public async updateDeals(login: string, data: DealsUpdateObject) {
        return this.apiManager.updateDeals(login, data);
    }

    public async loadDealsCampaignsByClient(client: string): Promise<DropdownChooserItem[]> {
        return this.apiManager.searchCampaignsForDeals(client);
    }

    public async clientsDealsLoadByLogin(login: string): Promise<string[]> {
        return this.apiManager.searchSubClientsForDeals(login);
    }

    public async acceptRejectDeals(login: string, dealIds: number[], isAccept: boolean) {
        return isAccept ? this.apiManager.acceptDeals(login, dealIds) : this.apiManager.rejectDeals(login, dealIds);
    }

    public getNewDealsCount(login: string): Promise<NewDealsCountResponse> {
        const dealsFilter = {
            statusContains: [dealStatusForFilter.RECEIVED]
        };

        return this.createQuery<DealsCount>(dealsCount, {
            variables: {
                login,
                dealsFilter
            }
        }).then((result: ApolloQueryResult<DealsCount>) => {
            return result.data.client.deals.totalCount;
        });
    }
}
